<template>
  <modal
    :show.sync="open"
    :size="step == WizardStep.SENSOR ? 'lg' : 'md'"
    hide-footer
    @onCloseIconClick="onModalClose(true)"
  >
    <h6 slot="header" class="modal-title">
      {{ getModalTitle() }}
    </h6>

    <div v-if="step == WizardStep.ORGANISATION">
      <base-alert v-if="selected.currentOrg && selected.currentOrg.length == 0">
        To view and update traps that do not yet belong to an organisation, click ‘Next’.
      </base-alert>

      <entity-picker
        key="organisation-step-picker"
        placeHolder="Start typing something..."
        apiEndpoint="getOrgs"
        :tableColumns="orgColums"
        :tableOptions="orgOptions"
        :canExport="false"
        :multiple="false"
        :selected.sync="selected.currentOrg"
        rootProp="organisations"
        @onChange="reset(false)"
        :hide-save="true"
      >
      </entity-picker>
    </div>

    <div v-if="step == WizardStep.SENSOR">
      <b-row>

        <b-col cols="6">
          <auto-complete
            placeHolder="Start typing something..."
            :searchParams="{
              default: 'barcode',
              params: [
                {
                  label: 'Barcode',
                  value: 'barcode'
                }
              ]
            }"
            @onTyped="onSensorSearch"
          />
        </b-col>
        <b-col cols="6">
          <csv-import-parser
            v-model="csv"
            :map-fields="['barcode']"
            headers="true"
            v-on:input="handleCsvLoad"
          />
          <div v-if="barcodesNotFound.length > 0">
            <h6 class='modal-title'>Barcodes not found:</h6>
            <i class="fas fa fa-copy mr-2 dz-clickable" v-on:click="()=>copyURL(barcodesNotFound.join(', '))" style="cursor: pointer;"/>
            <span class="dz-clickable" v-on:click="()=>copyURL(barcodesNotFound.join(', '))" style="cursor: pointer;">{{barcodesNotFound.join(', ')}}</span>
          </div>
          <div class="mt-2"><span style="font-weight: bold">Traps selected:</span> {{selected.sensors.length}}</div>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="6">
          <el-table
            :data="sensors.filter(s => !selected.sensors.includes(s))"
            element-loading-background="rgba(255, 255, 255, 0.8)!important"
            class="table-responsive table-flush mt-4"
            header-row-class-name="thead-light"
            max-height="500"
            style="width: 100%"
            row-key="id"
            stripe
          >
            <el-table-column prop="barcode" label="Barcode" width="130px" sortable />

            <el-table-column prop="currentStatus.status" label="Status" width="130px">
              <template v-slot="{ row }">
                <i
                  :class="`text-${SensorStatusHelper.getColour(
                    getSensorStatusId(row),
                    getSensorSubStatusId(row)
                  )} fas fa fa-${SensorStatusHelper.getIcon(getSensorStatusId(row), getSensorSubStatusId(row))}`"
                />
              </template>
            </el-table-column>

            <el-table-column label="" align="center" class="pr-0" width="auto">
              <template v-slot="{ $index, row }">
                <div class="d-flex justify-content-end">
                  <base-button class="btn-sm" type="success" @click="onSensorSelect($index, row)">
                    <i class="fas fa fa-plus" />
                  </base-button>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </b-col>

        <b-col cols="6">
          <el-table
            :data="selected.sensors"
            class="table-responsive table-flush mt-4"
            header-row-class-name="thead-light"
            max-height="500"
            style="width: 100%"
            row-key="id"
            stripe
          >
            <el-table-column prop="barcode" label="Barcode" width="130px" sortable />

            <el-table-column prop="currentStatus.status" label="Status" width="130px">
              <template v-slot="{ row }">
                <i
                  :class="`text-${SensorStatusHelper.getColour(
                    getSensorStatusId(row),
                    getSensorSubStatusId(row)
                  )} fas fa fa-${SensorStatusHelper.getIcon(getSensorStatusId(row), getSensorSubStatusId(row))}`"
                />
              </template>
            </el-table-column>

            <el-table-column label="" align="center" class="pr-0" width="auto">
              <template v-slot="{ $index, row }">
                <div class="d-flex justify-content-end">
                  <base-button class="btn-sm" type="danger" @click="onSensorRemove($index, row)">
                    <i class="fas fa fa-minus" />
                  </base-button>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </b-col>
      </b-row>
    </div>

    <div v-if="step == WizardStep.UPDATE">
      <base-input label="Select Date">
        <el-date-picker
          v-model="status.createdOn"
          value-format="timestamp"
          format="dd/MM/yyyy"
          type="date"
          placeholder="Set date changed"
          style="width: auto"
        />
      </base-input>
      <base-alert class="mb-2"> Pick a custom status date (defaults to now if left empty) </base-alert>
      <base-alert v-if="isDateOutOfRange()" type="warning" class="mt-2">
        At least one of the sensors you have picked has a date greater than the date you have selected
      </base-alert>

      <base-alert v-if="getInvalidSensorsByStatus().length > 0" type="warning" class="mt-2">
        At least one of the sensors you have picked already has that status and sub status
      </base-alert>

      <base-input v-if="selected.currentOrg.length <= 0" label="Pest type selector">
        <select class="form-control" v-model="selectedPestType">
          <option
            v-for="(item, index) in pestTypes"
            :value="item.id"
            :key="index">{{item.name}}</option>
        </select>
        <p v-if="selectedPestType === null" class="text-red">Pest type is required when assigning to organisation.</p>
      </base-input>

      <b-card no-bod class="tab-wrapper">
        <b-card-header header-tag="nav">
          <b-nav card-header tabs>
            <b-nav-item @click="tab = SensorEditTab.STATUS" :active="tab == SensorEditTab.STATUS">Status</b-nav-item>
            <b-nav-item @click="tab = SensorEditTab.ORGANISATION" :active="tab == SensorEditTab.ORGANISATION">
              Organisation
            </b-nav-item>
          </b-nav>
        </b-card-header>

        <div class="tab-container" v-if="tab == SensorEditTab.STATUS">
          <b-row class="mb-4">
            <b-col cols="12">
              <h4>Change Trap Status</h4>

              <b-row class="mb-2">
                <b-col cols="12">
                  <el-select
                    v-model.number="status.status"
                    @change="onSensorStatusChange"
                    placeholder="Pick a status"
                    style="width: 100%"
                  >
                    <el-option v-for="(name, value) in StatusMap" :key="value" :label="name" :value="+value">
                      <i
                        :class="`text-${SensorStatusHelper.getColour(
                          parseInt(value),
                          null
                        )} fas fa fa-${SensorStatusHelper.getIcon(parseInt(value), null)}`"
                      ></i>
                      {{ name }}
                    </el-option>
                  </el-select>
                </b-col>
              </b-row>

              <b-row v-if="status.subStatuses" class="mb-2">
                <b-col cols="12">
                  <el-select
                    v-model.number="status.subStatus"
                    placeholder="Pick a sub-status"
                    style="width: 100%"
                    :disabled="status == null"
                  >
                    <el-option v-for="(name, value) in status.subStatuses" :key="value" :label="name" :value="+value" />
                  </el-select>
                </b-col>
              </b-row>

              <b-row>
                <b-col cols="12">
                  <base-alert v-if="status == 2" class="mb-2">GPS location will be wiped from DB </base-alert>
                </b-col>
              </b-row>

              <b-row>
                <b-col cols="12">
                  <el-input v-model="status.note" type="textarea" placeholder="Optional note" />
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </div>

        <div class="tab-container" v-if="tab == SensorEditTab.ORGANISATION">
          <b-row v-if="selected.currentOrg.length > 0">
            <b-col cols="12">
              <div class="form-check">
                <input
                  id="is-removing-org-checkbox"
                  type="checkbox"
                  class="form-check-input"
                  v-model="selected.isRemovingOrg"
                />
                <label class="form-check-label" for="is-removing-org-checkbox">
                  <small>Remove Organisation</small>
                </label>
              </div>
            </b-col>
          </b-row>

          <base-alert class="mb-2" v-if="selected.isRemovingOrg">Note: Removing the org won't update the status automatically. </base-alert>

          <b-row v-if="selected.currentOrg.length <= 0">
            <b-col cols="12">
              <h4>Add Trap Organisation</h4>

              <auto-complete
                placeHolder="Start typing something..."
                :searchParams="{
                  default: 'name',
                  params: [
                    {
                      label: 'Name',
                      value: 'name'
                    }
                  ]
                }"
                @onTyped="onOrganisationSearch"
              />

              <el-table :data="organisations" stripe style="width: 100%" max-height="500">
                <el-table-column prop="name" label="Name" width="auto" sortable />

                <el-table-column min-width="120px" label="" align="center" class="pr-0">
                  <template v-slot="{ row }">
                    <div class="d-flex justify-content-end">
                      <base-button
                        class="btn-sm"
                        :disabled="isSameAsCurrentOrg(row.id)"
                        :type="`${isNewOrganisationSelected(row.id) ? 'danger' : 'success'}`"
                        @click="onNewOrganisationSelect(row)"
                      >
                        <i :class="`fas fa fa-${isNewOrganisationSelected(row.id) ? 'minus' : 'plus'}`" />
                      </base-button>
                    </div>
                  </template>
                </el-table-column>
              </el-table>
            </b-col>
          </b-row>
        </div>
      </b-card>
    </div>

    <div v-if="step == WizardStep.VERIFICATION">
      <b-row class="mb-3">
        <b-col cols="12">
          <h4>Date Changed:</h4>

          <p style="font-size: 14px; margin: 0">
            {{ getOrganisationDate() }}
          </p>

          <base-alert v-if="isDateOutOfRange()" type="warning" class="mt-2">
            Any sensors with a warning won't be updated, expand the row for more information.
          </base-alert>
        </b-col>
      </b-row>

      <b-row v-if="isEditingOrganisation()" class="mb-3">
        <b-col cols="12">
          <b-row>
            <b-col cols="12">
              <h4>Organisation Changes</h4>
            </b-col>
            <base-alert v-if="selectedPestType === null && selected.newOrg" type="warning" class="mt-2">
              When assigning an organisation, a pest type must be selected.
            </base-alert>
          </b-row>

          <b-row justify="space-around" align="middle" type="flex" style="display: flex; justify-content: space-around">
            <b-col cols="5">
              {{ selected.currentOrg.length > 0 ? selected.currentOrg[0].name : 'No Organisation' }}
            </b-col>
            <b-col cols="2">
              <i class="text-primary fas fa fa-angle-double-right fa-1x"></i>
            </b-col>
            <b-col cols="5">
              {{ selected.isRemovingOrg ? 'No Organisation' : selected.newOrg.name }}
            </b-col>
          </b-row>
          <b-row justify="space-around" align="middle" type="flex" style="display: flex; justify-content: space-around">
            <b-col cols="5">No Pest Type</b-col>
            <b-col cols="2">
              <i class="text-primary fas fa fa-angle-double-right fa-1x"></i>
            </b-col>
            <b-col cols="5">
              {{ selectedPestType ? pestTypes.find(type => type.id === selectedPestType).name : 'No Pest Type' }}
            </b-col>
          </b-row>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <h4>Traps Affected</h4>

          <el-table :data="selected.sensors" stripe style="width: 100%" max-height="500">
            <el-table-column type="expand">
              <template slot-scope="props">
                <p>
                  Sensor Status Created On:
                  {{
                    props.row.sensorStatusCreatedOn ? TimeHelper.formatDate(props.row.sensorStatusCreatedOn) : 'None'
                  }}
                </p>
                <p>
                  Org Created On:
                  {{ props.row.sensorOrgCreatedOn ? TimeHelper.formatDate(props.row.sensorOrgCreatedOn) : 'None' }}
                </p>

                <p>
                  Original Status: {{ StatusMap[props.row.currentStatus ? props.row.currentStatus.status : 0] }}
                  {{
                    props.row.currentStatus && props.row.currentStatus.subStatus ? '(' + SubStatusMap[props.row.currentStatus.subStatus] + ')' : ''
                  }}
                </p>

                <p>
                  Updated Status: {{ StatusMap[status.status] }}
                  {{ status.subStatus ? '(' + SubStatusMap[status.subStatus] + ')' : '' }}
                </p>
              </template>
            </el-table-column>

            <el-table-column prop="barcode" label="Barcode" min-width="50px" />

            <el-table-column prop="currentStatus.status" label="Status" min-width="65px">
              <template v-slot="{ row }">
                <b-row
                  justify="space-around"
                  align="middle"
                  type="flex"
                  style="display: flex; justify-content: space-evenly"
                >
                  <b-col>
                    <i
                      :title="SubStatusMap[row.currentStatus ? row.currentStatus.subStatus : 0]"
                      :style="[isSensorInvalid(row) ? { color: '#8898aa !important' } : null]"
                      :class="`text-${SensorStatusHelper.getColour(
                        getSensorStatusId(row),
                        getSensorSubStatusId(row)
                      )} fas fa fa-${SensorStatusHelper.getIcon(getSensorStatusId(row), getSensorSubStatusId(row))}`"
                    />
                  </b-col>

                  <b-col>
                    <i
                      :style="[isSensorInvalid(row) ? { color: '#8898aa !important' } : null]"
                      class="text-primary fas fa fa-angle-double-right fa-1x"
                    ></i>
                  </b-col>

                  <b-col>
                    <i
                      :title="SubStatusMap[status.subStatus]"
                      :style="[isSensorInvalid(row) ? { color: '#8898aa !important' } : null]"
                      :class="`text-${SensorStatusHelper.getColour(
                        status.status,
                        status.subStatus
                      )} fas fa fa-${SensorStatusHelper.getIcon(status.status, status.subStatus)}`"
                    />
                  </b-col>
                </b-row>
              </template>
            </el-table-column>

            <el-table-column min-width="30px">
              <template v-slot="{ row }">
                <div class="d-flex justify-content-end">
                  <i
                    v-if="isSensorInvalid(row)"
                    class="fas fa fa-exclamation-triangle text-warning"
                    :title="getInvalidSensorReason(row)"
                  />
                </div>
              </template>
            </el-table-column>

            <el-table-column min-width="40px" label="" align="center" class="pr-0">
              <template v-slot="{ $index, row }">
                <div class="d-flex justify-content-end">
                  <base-button class="btn-sm" type="danger" @click="onSensorRemove($index, row)">
                    <i class="fas fa fa-minus" />
                  </base-button>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </b-col>
      </b-row>
    </div>

    <template slot="footer">
      <base-button type="blank" @click="onModalClose(true)">Cancel</base-button>
      <base-button type="success" v-if="step > 0" @click="previous">Previous</base-button>
      <base-button type="success" v-if="step < 3" :disabled="!canGoNextStep()" @click="next" :loading="isNextLoading"
        >Next</base-button
      >
      <base-button type="success" v-if="step === 3" @click="onConfirmClick" :disabled="isConfirmDisabled()"
        >Confirm</base-button
      >
    </template>
  </modal>
</template>
<script>
import api from '@/api';
import { StatusMap, SubStatusMap } from '@/config/SensorStatuses';
import { TimeHelper } from '@/helpers/TimeHelper';
import CsvParser from '../CsvParser';
import {
  Button,
  Table,
  TableColumn,
  Notification,
  DatePicker,
  Select,
  Option,
  Dropdown,
  DropdownMenu,
  DropdownItem,
  Divider
} from 'element-ui';
import { orgColums, orgOptions } from '@/config/SensorUpdateModal';
import { SensorStatusHelper } from '@/helpers/SensorStatusHelper';
import { SensorStatusModel } from '@/models/sensor/sensorStatus';
import { debounce } from 'debounce';
import moment from 'moment';

const WizardStep = Object.freeze({
  ORGANISATION: 0,
  SENSOR: 1,
  UPDATE: 2,
  VERIFICATION: 3
});

const SensorEditTab = Object.freeze({
  STATUS: 0,
  ORGANISATION: 1
});

export default {
  name: 'sensors-update-modal',
  components: {
    [Button.name]: Button,
    [DatePicker.name]: DatePicker,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    [Select.name]: Select,
    [Option.name]: Option,
    [Notification.name]: Notification,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Divider.name]: Divider,
    'csv-import-parser':CsvParser
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      csv: null,
      WizardStep,
      SensorEditTab,
      SensorStatusHelper,
      TimeHelper,
      SubStatusMap,
      open: this.isOpen,
      step: WizardStep.ORGANISATION,
      status: new SensorStatusModel(),
      sensorOrganisation: [],
      allSensors: [],
      sensors: [],
      organisations: [],
      orgColums,
      orgOptions,
      pestTypes: [],
      selectedPestType: null,
      isNextLoading: false,
      search: {
        param: null,
        text: null
      },
      tab: SensorEditTab.STATUS,
      selected: {
        currentOrg: [],
        sensors: [],
        newOrg: null,
        isRemovingOrg: false,
      },
      barcodesNotFound: []
    };
  },
  created: async function () {
    this.$emit('loading', true);
    const results = await api.getOrgs({
      sort: this.orgOptions['default-sort'].prop,
      direction: this.orgOptions['default-sort'].order == 'ascending' ? 'asc' : 'desc',
      limit: this.orgOptions.limit
    });
    this.$emit('loading', false);
    this.orgsData = results.organisations;
    this.cursorData = results.cursor;
    this.getPestTypes();
  },
  watch: {
    isOpen(val) {
      this.open = val;
    },
    open(val) {
      this.$emit('update:is-open', val);
    }
  },
  computed: {
    StatusMap() {
      delete StatusMap[4];
      return StatusMap;
    }
  },
  mounted() {
    this.reset();
  },
  methods: {
    next: async function () {
      switch (this.step) {
        case WizardStep.ORGANISATION:
          try {
            this.isNextLoading = true;
            this.sensors = await this.getSensors();
            this.isNextLoading = false;
            this.step++;
          } catch (error) {
            this.onApiError(error);
          }
          break;
        case WizardStep.SENSOR:
          this.step++;
          break;
        case WizardStep.UPDATE:
          if (this.status.createdOn == null) {
            this.status.createdOn = Date.now();
          }

          this.step++;
          break;
        case WizardStep.VERIFICATION:
          break;
        default:
          return -1;
      }
    },
    previous: function () {
      switch (this.step) {
        case WizardStep.ORGANISATION:
          return;
        case WizardStep.SENSOR:
          this.barcodesNotFound = [];
          return this.step--;
        case WizardStep.UPDATE:
          this.step--;
          break;
        case WizardStep.VERIFICATION:
          this.step--;
          break;
        default:
          return -1;
      }
    },
    canGoNextStep: function () {
      switch (this.step) {
        case WizardStep.ORGANISATION:
          return true;
        case WizardStep.SENSOR:
          return this.selected.sensors.length > 0;
        case WizardStep.UPDATE:
          if (this.status.status == null) {
            return !(this.selected.isRemovingOrg == false && this.selected.newOrg == null);
          }

          // status validation
          if (this.status.status != null) {
            if (this.status.subStatuses && this.status.subStatus == null) {
              return false;
            }
          }

          // organisation validation (if removing organisation)
          if (this.selected.currentOrg.length > 0) {
            if (this.selected.isRemovingOrg && this.status.createdOn == null) {
              return false;
            }
          }

          return true;
        case WizardStep.VERIFICATION:
          return true;
        default:
          return false;
      }
    },
    getModalTitle: function () {
      if (this.selected.currentOrg == null || this.selected.currentOrg.length == 0) {
        return 'Update Traps';
      }

      return `Update ${this.selected.currentOrg[0].name} Traps`;
    },
    copyURL: async function (textToCopy) {
      try {
        await navigator.clipboard.writeText(textToCopy);
        this.success("Successfully copied to clipboard");
      } catch($e) {
        console.log('Cannot copy');
      }
    },
    onApiError: function (error) {
      this.$notify({
        title: `${error.name}`,
        message: `${error.message}`,
        type: 'error',
        duration: 2000
      });
    },
    success: function (message) {
      this.$notify({
        title: `Barcodes Copied!`,
        message: `${message}`,
        type: 'info',
        duration: 2000
      });
    },
    reset: function (close = false) {
      if (close) {
        this.open = false;
      }

      this.selected.sensors = [];
      this.selected.newOrg = null;
      this.selected.isRemovingOrg = false;
      this.status = new SensorStatusModel();

      const user = JSON.parse(localStorage.getItem('profile'));
      this.status.createdOn = moment().startOf('day').unix() * 1000;
      this.status.user = {
        id: user.id
      };
    },
    onModalClose: function (close = false) {
      this.reset(close);

      this.step = WizardStep.ORGANISATION;
      this.selected.currentOrg = [];
      this.barcodesNotFound = [];
    },
    getSensors: async function (barcode) {
      const id = this.selected.currentOrg[0] != null ? this.selected.currentOrg[0].id : 'null';
      const { sensors } = await api.getSensors({
        filter: `organisation:${id}${barcode ? `,barcode:${barcode}` : ''}`,
        sort: 'createdOn',
        direction: 'desc',
        limit: 9999
      });

      return sensors.filter((s1) => {
        return !this.selected.sensors.some(s2 => s1.id === s2.id);
      });
    },
    onSensorSearch: debounce(async function (barcode) {
      const sensors = await this.getSensors(barcode);
      this.sensors = sensors;
    }, 50),
    isSensorSelected: function (id) {
      const found = this.selected.sensors.filter((x) => x.id === id);
      return found.length > 0;
    },
    onSensorSelect: function (index, sensor) {
      // this.sensors.splice(index, 1);
      this.selected.sensors.push(sensor);
    },
    onSensorRemove: function (index, sensor) {
      this.selected.sensors.splice(index, 1);
    },
    onSensorStatusChange: function () {
      this.status.subStatus = null;
    },
    handleCsvLoad: function () {
      let filteredTraps = this.sensors.filter((s) => this.csv.includes(s.barcode));
      filteredTraps = filteredTraps.filter((s1) => !this.selected.sensors.some((s2) => s1.barcode === s2.barcode));
      this.barcodesNotFound = this.csv.filter((b) => !this.sensors.some(s => s.barcode === b));
      this.selected.sensors = this.selected.sensors.concat(filteredTraps);
    },
    getInvalidSensorStatusDates: function (date) {
      return this.isEditingSensor() ? this.selected.sensors.filter((sensor) =>
        moment
          .unix(date / 1000)
          .startOf('day')
          .isBefore(moment.unix(sensor.sensorStatusCreatedOn).startOf('day').toDate())
      ) : [];
    },
    getInvalidSensorOrgDates: function (date) {
      return this.isEditingOrganisation() ? this.selected.sensors.filter((sensor) =>
        moment
          .unix(date / 1000)
          .startOf('day')
          .isBefore(moment.unix(sensor.sensorOrgCreatedOn).startOf('day').toDate())
      ) : [];
    },
    getOrganisations: async function (name) {
      const { organisations } = await api.getOrgs({
        filter: `name:${name}`
      });
      return organisations;
    },
    onOrganisationSearch: debounce(async function (name) {
      const organisations = await this.getOrganisations(name);
      this.organisations = organisations;
    }, 50),
    isNewOrganisationSelected: function (id) {
      if (!this.selected.newOrg) {
        return false;
      }

      return this.selected.newOrg.id == id;
    },
    onNewOrganisationSelect: function (row) {
      if (this.isNewOrganisationSelected(row.id)) {
        this.selected.newOrg = null;
      } else {
        this.selected.newOrg = row;
      }
    },
    isSameAsCurrentOrg: function (id) {
      if (this.selected.currentOrg == null || this.selected.currentOrg.length == 0) {
        return false;
      }

      return this.selected.currentOrg[0].id == id;
    },
    getSensorStatusId: function (sensor) {
      return sensor.currentStatus != null ? sensor.currentStatus.status : null;
    },
    getSensorSubStatusId: function (sensor) {
      return sensor.currentStatus != null ? sensor.currentStatus.subStatus : null;
    },
    isEditingSensor: function () {
      return this.status != null && this.status.status != null;
    },
    isEditingOrganisation: function () {
      return this.selected.isRemovingOrg || this.selected.newOrg != null;
    },
    getOrganisationDate: function () {
      return TimeHelper.formatDate(this.status.createdOn == null ? Date.now() / 1000 : this.status.createdOn / 1000);
    },
    isDateOutOfRange: function () {
      if (this.status.createdOn == null) {
        return false;
      }

      return (
        this.getInvalidSensorStatusDates(this.status.createdOn).length > 0 ||
        this.getInvalidSensorOrgDates(this.status.createdOn).length > 0
      );
    },
    getInvalidSensorsByStatus: function () {
      return this.selected.sensors.filter(
        (sensor) =>
          sensor.currentStatus != null &&
          sensor.currentStatus.status == this.status.status &&
          sensor.currentStatus.subStatus == this.status.subStatus
      );
    },
    isSensorInvalid: function (sensor) {
      const sensors = this.getInvalidSensorStatusDates(this.status.createdOn);

      let found = sensors.find((invalid) => invalid.id === sensor.id);
      if (!found) {
        const orgs = this.getInvalidSensorOrgDates(this.status.createdOn);
        found = orgs.find((invalid) => invalid.id === sensor.id);
      }

      if (!found) {
        const statuses = this.getInvalidSensorsByStatus();
        found = statuses.find((invalid) => invalid.id === sensor.id);
      }

      return found != null;
    },
    getInvalidSensorReason: function (sensor) {
      let reason = [];

      let found = this.getInvalidSensorStatusDates(this.status.createdOn).find((invalid) => invalid.id == sensor.id);
      if (found) {
        reason.push('Invalid Status Date');
      }

      found = this.getInvalidSensorOrgDates(this.status.createdOn).find((invalid) => invalid.id == sensor.id);
      if (found) {
        reason.push('Invalid Org Date');
      }

      found = this.getInvalidSensorsByStatus().find((invalid) => invalid.id == sensor.id);
      if (found) {
        reason.push('Invalid Status');
      }

      return reason.join(', ');
    },
    isConfirmDisabled: function () {
      let isDisabled = this.getValidSensors().length == 0;
      if (!isDisabled && this.isEditingOrganisation() && this.selected.newOrg) {
        isDisabled = this.selectedPestType === null;
      }
      return isDisabled;
    },
    getValidSensors: function () {
      return this.selected.sensors.filter((sensor) => !this.isSensorInvalid(sensor));
    },
    onConfirmClick: async function () {
      const sensors = this.getValidSensors().map((sensor) => sensor.id);

      try {
        const payload = {
          userId: this.status.user.id,
          removedOrganisation: this.selected.isRemovingOrg,
          description: this.status.note,
          createdOn: this.status.createdOn / 1000,
          sensors: sensors
        };

        if (this.status.status) {
          payload.sensorStatus = this.status.status;
        }

        if (this.status.subStatus) {
          payload.sensorSubStatus = this.status.subStatus;
        }

        if (this.selected.newOrg) {
          payload.organisationId = this.selected.newOrg.id;
          payload.pestTypeId = this.selectedPestType
        }

        await api.batchTrapUpdate(payload);

        this.$notify({
          title: 'Success',
          message: 'Traps have been updated successfully',
          type: 'success',
          duration: 2500
        });

        this.onModalClose(true);
      } catch (error) {
        this.onApiError(error);
      }
    },
    getPestTypes() {
      api.getPestTypes().then((response) => {
        this.pestTypes = response.records
      })
    },
  }
};
</script>
